import React, { useEffect, useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import { buildTextFieldSx, fieldLabelSx } from '../common/formFieldStyles';
import { deleteStoreCompletely } from '../../services/storesManagementService';

const DeleteStoreDialog = ({
  store,
  open,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const [confirmationName, setConfirmationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const expectedName = store?.name?.trim() ?? '';
  const nameMatches = confirmationName.trim() === expectedName && expectedName.length > 0;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setConfirmationName('');
    setError(null);
    setLoading(false);
    return undefined;
  }, [open, store?.storeId]);

  const handleConfirm = async () => {
    if (!store || !nameMatches || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteStoreCompletely(store.storeId);
      onDeleted(store.storeId);
      onClose();
    } catch (deleteError) {
      console.error('Failed to delete store:', deleteError);
      setError(deleteError?.response?.data?.message || t('storesManagement.stores.delete.error'));
      setLoading(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={t('storesManagement.stores.delete.title')}
      message={store ? t('storesManagement.stores.delete.message', { name: store.name }) : ''}
      confirmText={t('storesManagement.stores.delete.confirm')}
      confirmColor="error"
      loading={loading}
      confirmDisabled={!nameMatches}
      closeOnConfirm={false}
    >
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={fieldLabelSx}>
          {t('storesManagement.stores.delete.confirmLabel')}
        </Typography>
        <TextField
          fullWidth
          value={confirmationName}
          onChange={(event) => setConfirmationName(event.target.value)}
          disabled={loading}
          autoComplete="off"
          sx={buildTextFieldSx()}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Box>
    </ConfirmDialog>
  );
};

export default DeleteStoreDialog;
